import { CalendarDays, Clock, Trash2, UserRound } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  useDeleteTask,
  useSaveTask,
  useSetTaskStatus,
  type Project,
  type Task,
  type TaskStatus,
} from "@/api/queries";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Field } from "@/components/common/Field";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import { errorText, fieldError } from "@/lib/errors";
import { formatDuration } from "@/lib/format";

import { formatDueDate, hoursToMinutes, minutesToHours, taskStatuses } from "./lib";
import { TaskStatusBadge, TaskStatusSelect } from "./TaskStatus";

type TaskDialogProps = {
  project: Project;
  /** Missing: a new task. */
  task?: Task;
  /** Status of a new task (the board column it was created from). */
  initialStatus?: TaskStatus;
  /** The caller may move the task (assignee or manager). */
  canMove?: boolean;
  onClose: () => void;
};

/** Task form for project managers; a read-only card for everybody else. */
export function TaskDialog(props: TaskDialogProps) {
  return props.project.can_manage && props.project.status === "active" ? (
    <TaskForm {...props} />
  ) : (
    <TaskView {...props} />
  );
}

function TaskForm({ project, task, initialStatus, onClose }: TaskDialogProps) {
  const { t } = useTranslation();
  const save = useSaveTask();
  const [title, setTitle] = useState(task?.title ?? "");
  const [description, setDescription] = useState(task?.description ?? "");
  const [assignee, setAssignee] = useState(task?.assignee_id ?? "");
  const [status, setStatus] = useState<TaskStatus>(task?.status ?? initialStatus ?? "todo");
  const [dueDate, setDueDate] = useState(task?.due_date ?? "");
  const [estimate, setEstimate] = useState(minutesToHours(task?.estimate_minutes));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const estimateMinutes = hoursToMinutes(estimate);
  const estimateInvalid = Number.isNaN(estimateMinutes);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (estimateInvalid) return;
    try {
      await save.mutateAsync({
        projectId: project.id,
        taskId: task?.id,
        body: {
          title: title.trim(),
          description: description.trim(),
          assignee_id: assignee || undefined,
          status,
          due_date: dueDate || undefined,
          estimate_minutes: estimateMinutes,
        },
      });
      onClose();
    } catch {
      // Shown below.
    }
  };

  const titleError = fieldError(t, save.error, "title");
  const assigneeError = fieldError(t, save.error, "assignee_id");
  const estimateError = estimateInvalid
    ? t("fieldErrors.invalid")
    : fieldError(t, save.error, "estimate_minutes");
  const dueError = fieldError(t, save.error, "due_date");
  const otherError = save.error && !titleError && !assigneeError && !estimateError && !dueError;

  if (confirmDelete && task) {
    return <DeleteTaskDialog task={task} onClose={() => setConfirmDelete(false)} onDeleted={onClose} />;
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={task ? t("tasks.editTitle") : t("tasks.createTitle")}
        description={project.name}
        className="w-[min(92vw,560px)]"
      >
        <form className="flex flex-col gap-4 p-5" onSubmit={(e) => void submit(e)}>
          <Field label={t("tasks.fields.title")} error={titleError}>
            <Input
              required
              autoFocus
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field label={`${t("tasks.fields.description")} (${t("common.optional")})`}>
            <textarea
              rows={4}
              maxLength={10000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-lg border bg-surface px-3 py-2 shadow-card"
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("tasks.fields.assignee")} error={assigneeError}>
              <NativeSelect
                className="h-10 w-full"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
              >
                <option value="">{t("tasks.unassigned")}</option>
                {project.members.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.full_name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={t("tasks.fields.status")}>
              <NativeSelect
                className="h-10 w-full"
                value={status}
                onChange={(e) => setStatus(e.target.value as TaskStatus)}
              >
                {taskStatuses.map((s) => (
                  <option key={s} value={s}>
                    {t(`tasks.status.${s}`)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={`${t("tasks.fields.dueDate")} (${t("common.optional")})`} error={dueError}>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
            <Field label={`${t("tasks.fields.estimate")} (${t("common.optional")})`} error={estimateError}>
              <Input inputMode="decimal" value={estimate} onChange={(e) => setEstimate(e.target.value)} />
            </Field>
          </div>
          {otherError && <Alert variant="danger">{errorText(t, save.error)}</Alert>}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {task ? (
              <Button
                type="button"
                variant="ghost"
                className="text-danger"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 /> {t("tasks.delete")}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  {t("common.cancel")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={save.isPending || !title.trim() || estimateInvalid}>
                {task ? t("common.save") : t("common.create")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TaskView({ project, task, canMove, onClose }: TaskDialogProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const move = useSetTaskStatus();
  if (!task) return null;
  const assignee = project.members.find((m) => m.user_id === task.assignee_id);
  const editable = canMove && project.status === "active";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={task.title} description={project.name} className="w-[min(92vw,560px)]">
        <div className="flex flex-col gap-4 p-5">
          <dl className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2.5">
            <Row label={t("tasks.fields.status")}>
              {editable ? (
                <TaskStatusSelect
                  label={t("tasks.statusOf", { name: task.title })}
                  value={task.status}
                  disabled={move.isPending}
                  onChange={(status) => move.mutate({ taskId: task.id, status })}
                  className="w-44"
                />
              ) : (
                <TaskStatusBadge status={task.status} />
              )}
            </Row>
            <Row label={t("tasks.fields.assignee")} icon={<UserRound />}>
              {assignee?.full_name ?? <span className="text-muted">{t("tasks.unassigned")}</span>}
            </Row>
            <Row label={t("tasks.fields.dueDate")} icon={<CalendarDays />}>
              {task.due_date ? formatDueDate(task.due_date, lang) : "—"}
            </Row>
            <Row label={t("tasks.fields.tracked")} icon={<Clock />}>
              <span className="tabular-nums">
                {formatDuration(task.tracked_seconds, lang)}
                {task.estimate_minutes !== undefined &&
                  ` / ${formatDuration(task.estimate_minutes * 60, lang)}`}
              </span>
            </Row>
          </dl>
          {task.description && <p className="whitespace-pre-wrap">{task.description}</p>}
          {move.error && <Alert variant="danger">{errorText(t, move.error)}</Alert>}
          <p className="text-xs text-muted">{t("tasks.readOnly")}</p>
          <div className="flex justify-end">
            <DialogClose asChild>
              <Button variant="secondary">{t("common.close")}</Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, icon, children }: { label: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <>
      <dt className="flex items-center gap-1.5 text-muted [&_svg]:size-4">
        {icon}
        {label}
      </dt>
      <dd>{children}</dd>
    </>
  );
}

export function DeleteTaskDialog({
  task,
  onClose,
  onDeleted,
}: {
  task: Task;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const { t } = useTranslation();
  const remove = useDeleteTask();
  return (
    <ConfirmDialog
      danger
      title={t("tasks.deleteTitle", { name: task.title })}
      description={t("tasks.deleteText")}
      confirmLabel={t("common.delete")}
      errorText={(err) => errorText(t, err)}
      onConfirm={async () => {
        await remove.mutateAsync(task.id);
        onDeleted?.();
      }}
      onClose={onClose}
    />
  );
}
